import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

/**
 * UtmTracker — fires a single `/api/analytics/utm-hit` POST per browser
 * session whenever the landing URL carries UTM or `ref` parameters.
 *
 * Designed for the tier-up "Share on X" attribution chain:
 *   ?utm_source=email&utm_medium=tier_share&utm_campaign=tier_<tier>&ref=<code>
 *
 * Anonymous + opt-out friendly:
 *   • No cookies, no localStorage of PII
 *   • SessionStorage key only — cleared when tab closes
 *   • POST is fire-and-forget; failures never bubble to the UI
 */
export default function UtmTracker() {
  const loc = useLocation();

  // Persist the ref in a layout effect so it beats page-level effects
  // that strip search params (e.g. Elders' URL-sync effect). Layout
  // effects always run before regular effects — no matter the component
  // tree position — so the ambassador attribution never gets clobbered.
  useLayoutEffect(() => {
    try {
      const ref = new URLSearchParams(loc.search).get("ref");
      if (!ref) return;
      localStorage.setItem("cauldron_ref", ref);
      localStorage.setItem(
        "cauldron_ref_expires",
        String(Date.now() + 30 * 24 * 60 * 60 * 1000),
      );
    } catch { /* private-mode / storage-full — non-fatal */ }
  }, [loc.search]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(loc.search);
      const source = params.get("utm_source");
      const medium = params.get("utm_medium");
      const campaign = params.get("utm_campaign");
      const ref = params.get("ref");
      if (!source && !medium && !campaign && !ref) return;

      // De-dupe per session — same campaign+path only records once
      const key = `utm:${campaign || "_"}:${ref || "_"}:${loc.pathname}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");

      api
        .post("/analytics/utm-hit", {
          source,
          medium,
          campaign,
          ref,
          path: loc.pathname,
          referer: document.referrer || null,
        })
        .catch(() => { /* fire-and-forget */ });
    } catch { /* defensive — never block the page */ }
  }, [loc.search, loc.pathname]);

  return null;
}
